import React from "react";
import { Principle, PrincipleColor, Principles } from "../boh/principles";
import { Save } from "../boh/save";
import { PrincipleList } from "../components/principleList";
import {
  FilterRecipesBySkills,
  GetRecipesByResult,
  Recipe,
  Recipes,
} from "../boh/recipes";
import { Book, FindBooksThatSpawnId } from "../boh/book";
import { Item, GetItemsByConsiderSpawnId } from "../boh/items";
import { Memories, Memory } from "../boh/memories";
import { GetCraftingHintString } from "../boh/recipes_hints";

export function LessonView({ save }: { save: Save }) {
  const [principles, setPrinciples] = React.useState<Set<Principle>>(new Set());
  const [manualSelected, setSelectedMems] = React.useState<Set<string>>(
    new Set(),
  );

  const selectedMemories = new Set(manualSelected);

  const toggleMem = (id: string) => {
    if (selectedMemories.has(id)) selectedMemories.delete(id);
    else selectedMemories.add(id);
    setSelectedMems(new Set(selectedMemories));
  };

  const matchingMemories = Memories.filter((memory) => {
    for (const principle of principles) {
      if (memory[principle]) {
        if (save.items.some((item) => item.id == memory.id)) {
          selectedMemories.add(memory.id);
        }
        return true;
      }
    }
    return false;
  });

  // We should filter memories by what is actually CRAFTABLE.
  // consider: I have access to the item
  // read: I have the book (and IDEALLY, mastered it already)
  // craft: I AT LEAST have the skill (and ideally the things to make it)

  // TODO: weather??
  // TODO: TALKING TO ASSISTANTS! This is a good way to get stuff early
  const recipesBySkills = FilterRecipesBySkills(save.skills); // not working??

  const recipeMap = new Map<string, Recipe[]>(
    matchingMemories.map((m) => [
      m.id,
      GetRecipesByResult(m.id, recipesBySkills),
    ]),
  );
  // console.log("Matching recipes", recipeMap);

  const considerMap = new Map<string, Item[]>(
    matchingMemories.map((m) => [
      m.id,
      GetItemsByConsiderSpawnId(m.id, save.items),
    ]),
  );
  // console.log("Things to consider", considerMap);

  const bookMap = new Map<string, Book[]>(
    matchingMemories.map((m) => [m.id, FindBooksThatSpawnId(m.id, save.books)]),
  );

  const memoriesYouCanGet = matchingMemories.filter(
    ({ id }) =>
      selectedMemories.has(id) ||
      recipeMap.get(id)?.length ||
      considerMap.get(id)?.length ||
      bookMap.get(id)?.length,
  );

  // Group memories
  const regular = memoriesYouCanGet.filter((m) => !m.persistent && !m.numen);
  const persistent = memoriesYouCanGet.filter((m) => m.persistent && !m.numen);
  const numen = memoriesYouCanGet.filter((m) => m.numen);

  return (
    <div>
      <div>
        Lessons:{" "}
        {[...principles].map((p) => (
          <span style={{ color: PrincipleColor(p) }}>{p}</span>
        ))}
        {selectedMemories.size ? ` (${selectedMemories.size} selected)` : null}
      </div>
      {/* Search */}
      <div style={{ display: "flex", flexDirection: "row" }}>
        <select
          size={13}
          style={{
            flex: "none",
            overflow: "hidden",
            height: "fit-content",
            background: "#282c34",
            margin: 5,
          }}
          multiple
          value={[...principles]}
          onChange={(ev) => {
            setSelectedMems(new Set());
            setPrinciples(
              new Set(
                [...ev.target.selectedOptions].map((s) => s.value as Principle),
              ),
            );
          }}
        >
          {Principles.map((p) => (
            <option
              key={p}
              value={p}
              style={{
                background: "rgba(0,0,0,.2)",
                color: PrincipleColor(p),
                fontSize: 16,
                padding: "5px 10px",
              }}
            >
              {p}
            </option>
          ))}
        </select>
        {/* Show memories */}
        <div>
          <MemoryList
            title="Memories"
            memories={regular}
            save={save}
            madeBefore={save.madeBefore}
            selectedMemories={selectedMemories}
            toggle={toggleMem}
          />
          <MemoryList
            title="Persistent"
            memories={persistent}
            save={save}
            madeBefore={save.madeBefore}
            selectedMemories={selectedMemories}
            toggle={toggleMem}
          />
          <MemoryList
            title="Numen"
            memories={numen}
            save={save}
            madeBefore={save.madeBefore}
            selectedMemories={selectedMemories}
            toggle={toggleMem}
          />
        </div>
      </div>
    </div>
  );
}

function MemoryList({
  memories,
  title,
  madeBefore,
  toggle,
  selectedMemories,
  save,
}: {
  memories: Memory[];
  title?: string;
  madeBefore?: Set<string>;
  selectedMemories?: Set<string>;
  toggle?: (id: string) => void;
  save?: Save;
}) {
  if (!memories.length) return null;

  return (
    <div style={{ padding: 10 }}>
      {title ? (
        <div>
          {title} ({memories.length})
        </div>
      ) : null}
      {memories.map((m, i) => {
        const hints = GetCraftingHintString(m, save);
        const canRead = hints.indexOf("Read:") >= 0;
        const canCraft = hints.indexOf("Craft:") >= 0;
        const canConsider = hints.indexOf("Consider:") >= 0;
        const canConsiderConsume = hints.indexOf("Consider (consume):") >= 0;

        let color = "";
        let prefix = "";
        if (canConsider) {
          color = "lightgreen";
          prefix = "(consider) ";
        } else if (canRead) {
          color = "lightblue";
          prefix = "(read) ";
        } else if (canCraft) {
          // color = "white";
          prefix = "(craft) ";
        } else if (canConsiderConsume) {
          color = "brown";
          prefix = "(consume) ";
        } else color = "grey";

        return (
          <div key={m.id} title={hints}>
            <label>
              <input
                type="checkbox"
                checked={selectedMemories?.has(m.id)}
                onChange={() => toggle?.(m.id)}
              />
              <span
                style={{
                  color: color || (madeBefore?.has(m.id) ? "green" : undefined),
                }}
              >
                {prefix}
                {m.label}
              </span>
            </label>{" "}
            <PrincipleList {...m} />
          </div>
        );
      })}
    </div>
  );
}
